// gaji.service — orkestrasi Penggajian: guard izin, alur 3 langkah, dan efek uangnya.
//
// Alur sengaja disamakan dengan pembayaran komisi (payout.service): buat → setujui → bayar,
// dan efek uang HANYA terjadi di langkah bayar. Itu yang membuat "sudah disetujui" tidak sama
// dengan "uang sudah keluar", sehingga Laba-Rugi tak pernah mencatat gaji yang belum dibayar.
import { currentUser } from '@core/session';
import { bisaUbah } from '@core/perms';
import { logAudit } from '@core/audit';
import { addExpense } from './finance.repo';
import { tambahMutasi } from '@modules/payments/akunkas.repo';
import * as repo from './gaji.repo';

const role = (): string => currentUser()?.role ?? '';
const isMgr = (): boolean => ['Owner', 'Manager'].includes(role());

// Dua lapis: peran (kasar) DAN sistem Hak Akses (bisa dicabut per orang). Guard ada di service,
// bukan hanya di view — layar bisa dilewati, service tidak.
/** Membuat & mengubah slip: manajemen + Admin Finance (sama dengan pengaju komisi). */
export const bolehKelola = (): boolean => (isMgr() || role() === 'Admin Finance') && bisaUbah('penggajian.slip');
/** Menyetujui: manajemen saja — dan Finance dikunci di TERKUNCI supaya tak bisa dinaikkan. */
export const bolehSetujui = (): boolean => isMgr() && bisaUbah('penggajian.setujui');
/** Membayar (efek uang: beban 'Gaji'). */
export const bolehBayar = (): boolean => (isMgr() || role() === 'Admin Finance') && bisaUbah('penggajian.bayar');

export async function simpanSlip(d: repo.SlipInput): Promise<void> {
  if (!bolehKelola()) throw new Error('Anda tak berwenang membuat slip gaji.');
  await repo.simpanSlip(d);
  void logAudit({ action: 'PAYROLL_SAVE', module: 'gaji', refId: `${d.periode}/${d.emp_nama}`, outlet: d.outlet, after: d });
}

export async function setujui(slip: repo.Slip): Promise<void> {
  if (!bolehSetujui()) throw new Error('Hanya Owner/Manager yang boleh menyetujui slip gaji.');
  // Pemisahan wewenang juga ditegakkan trigger DB (0148) — ini hanya supaya pesannya ramah.
  if (slip.dibuat_by && slip.dibuat_by === currentUser()?.nama) {
    throw new Error('Yang membuat slip tidak boleh menyetujuinya sendiri.');
  }
  await repo.setujuiSlip(slip.id);
  void logAudit({ action: 'PAYROLL_APPROVE', module: 'gaji', refId: slip.no ?? String(slip.id), outlet: slip.outlet, after: { netto: slip.netto } });
}

/**
 * Bayar slip → catat beban kategori 'Gaji' sekaligus. Bebannya dibuat DULU: kalau pencatatan
 * beban gagal, slip tetap 'approved' dan bisa diulang. Urutan sebaliknya akan menghasilkan gaji
 * yang tercatat terbayar tanpa pernah masuk buku.
 */
export async function bayar(slip: repo.Slip, via: string, akunId: number | null): Promise<void> {
  if (!bolehBayar()) throw new Error('Anda tak berwenang membayar gaji.');
  // Baca ulang dari DB: layar bisa saja dibuka sejak tadi, dan yang dibebankan harus netto yang
  // benar-benar tersimpan — bukan angka yang sudah basi di memori.
  const kini = await repo.getSlip(slip.id);
  if (!kini) throw new Error('Slip tidak ditemukan.');
  if (kini.status === 'paid') throw new Error('Slip ini sudah dibayar.');
  if (kini.status !== 'approved') throw new Error('Slip harus disetujui dulu sebelum dibayar.');

  // KLAIM DULU, beban belakangan. Dua tab yang sama-sama menekan Bayar hanya menghasilkan satu
  // beban: yang kalah gagal mengklaim. Urutan sebaliknya (beban dulu) pernah jadi cara paling
  // mudah mencatat gaji dua kali.
  if (!await repo.klaimBayar(kini.id, via)) throw new Error('Slip ini baru saja dibayar orang lain — muat ulang layarnya.');

  const hariIni = new Date().toISOString().slice(0, 10);
  try {
    // `addExpense` mengembalikan NOMOR DOKUMEN, bukan id — tautannya satu arah lewat catatan
    // beban yang memuat nomor slip: dari beban di buku, mencari slip siapa.
    const noDok = await addExpense({
      tgl_iso: hariIni, outlet: kini.outlet, kategori: 'Gaji',
      nama: `Gaji ${kini.emp_nama} — ${kini.periode}`, jumlah: kini.netto,
      catatan: `Slip ${kini.no ?? kini.id}${via ? ` · via ${via}` : ''}`,
      akun_id: akunId,
    });
    // Beban tanpa akun kas = HUTANG USAHA di aplikasi ini: tidak mengurangi kas di Arus Kas
    // maupun Posisi. Jadi gaji yang benar-benar dibayar HARUS menyebut akun sumbernya, kalau
    // tidak saldo kas melebih sebesar seluruh gaji tiap bulan sementara slipnya bertulis
    // "✔ dibayar". Pola & alasannya sama dengan menu Beban.
    if (akunId && kini.netto > 0) {
      try {
        await tambahMutasi({ tgl: hariIni, akunId, arah: 'keluar', jumlah: kini.netto, jenis: 'beban',
          catatan: `Gaji ${kini.emp_nama} ${kini.periode}`, refTabel: 'expenses', refId: noDok || null, outlet: kini.outlet });
      } catch (e2) {
        // Bebannya sudah tercatat; hanya buku kasnya yang tertinggal. Jangan batalkan pembayaran
        // karena ini — beri tahu, biar dicocokkan di menu Akun Kas.
        console.warn('[gaji] mutasi kas gagal:', (e2 as Error).message);
      }
    }
  } catch (e) {
    // Beban gagal → kembalikan klaimnya, jangan tinggalkan slip "dibayar" tanpa catatan uang.
    await repo.batalKlaimBayar(kini.id);
    throw new Error('Gagal mencatat beban gaji: ' + (e as Error).message);
  }
  void logAudit({ action: 'PAYROLL_PAID', module: 'gaji', refId: kini.no ?? String(kini.id), outlet: kini.outlet, after: { netto: kini.netto, via, akunId } });
}

export async function hapus(slip: repo.Slip): Promise<void> {
  if (!isMgr() || !bisaUbah('penggajian.slip')) throw new Error('Anda tak berwenang menghapus slip.');
  if (slip.status === 'paid') throw new Error('Slip yang sudah dibayar tidak bisa dihapus — itu catatan uang yang sudah keluar.');
  await repo.hapusSlip(slip.id);
  void logAudit({ action: 'PAYROLL_DELETE', module: 'gaji', refId: slip.no ?? String(slip.id), outlet: slip.outlet, before: slip });
}
